import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { apolloClient } from 'lib/core/services/magento/client'
import md5 from 'blueimp-md5'
import { localStorage } from 'lib/core/services/localStorage'
import { PWAThunk } from 'lib/core/data'
import { MagentoCustomer } from 'lib/core/data/customer/me/types'
import { fetchCustomerCartId } from 'lib/core/data/customer/cartId'
import { refreshCustomerToken } from 'lib/core/data/customer/tokenRefresh'
import { me, validateSessionIsActive } from 'lib/core/data/customer/me'
import { fetchPaymentTokens } from 'lib/core/data/customer/paymentTokens'
import { fetchWishlist } from 'lib/core/data/customer/wishlist'
import { Session } from './types'

export const initialState: Session = {
  data: undefined,
  init: false
}

const slice = createSlice({
  name: 'auth/session',
  initialState,
  reducers: {
    init (state, action: PayloadAction<MagentoCustomer | undefined>) {
      state.init = true
      state.data = action?.payload
    },

    unset (state) {
      state.init = true
      state.data = undefined
    }
  }
})

export const reducer = slice.reducer

export const { init, unset } = slice.actions

let validateSessionIsActiveInterval: number | undefined

export const setSession = (token?: string, scheduleRefresh?: number): PWAThunk => {
  return async (dispatch, getState) => {
    let payload: MagentoCustomer | undefined

    if (token) {
      localStorage.set('authToken', token)
      await dispatch(me())
      const state = getState()
      const customer = state.customer.me.payload

      if (customer) {
        window.clearInterval(validateSessionIsActiveInterval)
        validateSessionIsActiveInterval = window.setInterval(() => {
          dispatch(validateSessionIsActive(validateSessionIsActiveInterval))
        }, 15000)

        if (scheduleRefresh) {
          const timeoutId = setTimeout(() => dispatch(refreshCustomerToken()), scheduleRefresh - Date.now())
          localStorage.set('authTokenRefreshTimeoutId', timeoutId)
          // eslint-disable-next-line no-console
          console.info('Scheduling refresh....', new Date(scheduleRefresh))
        } else {
          dispatch(refreshCustomerToken())
        }

        payload = customer
        dispatch(fetchWishlist())
        dispatch(fetchPaymentTokens())
        dispatch(fetchCustomerCartId({
          guestCartId: localStorage.get('cartToken')
        }))
        localStorage.set('user', { email: md5(payload?.email ?? '') })
        localStorage.remove('cartToken')
      } else {
        localStorage.remove('authToken')
        localStorage.remove('user')
      }
    }
    dispatch(init(payload))
  }
}

export const unsetSession = (): PWAThunk => {
  return async (dispatch) => {
    const timeoutId = localStorage.get('authTokenRefreshTimeoutId')
    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    window.clearInterval(validateSessionIsActiveInterval)
    validateSessionIsActiveInterval = undefined

    localStorage.remove('authToken')
    localStorage.remove('authTokenRefreshTimeoutId')
    await apolloClient.clearStore() // https://www.apollographql.com/docs/react/networking/authentication/
    dispatch(unset())
  }
}

export const checkSession = (): PWAThunk => {
  return async (dispatch) => {
    const token = localStorage.get('authToken')
    await dispatch(setSession(token))
  }
}
